import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAppStore } from '../store/Store';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import StarIcon from '../icons/StarIcon';
import ReturnIcon from '../icons/ReturnIcon';
import { BASE_URL } from '../apis/constant';

// Reuse the exact same Tag component from ProductCard
const Tag = ({ text, color }) => {
  const baseClasses = 'absolute top-3 left-3 px-2 py-0.5 text-xs font-bold rounded-full text-white tracking-wider z-10';
  return <div className={`${baseClasses} ${color}`}>{text}</div>;
};

// Reuse the exact same PriceDisplay from ProductCard
const PriceDisplay = ({ price, offerPrice }) => (
  <div className="flex items-center gap-3">
    <span className="text-lg sm:text-xl font-bold text-gray-900">
      {`${(offerPrice ?? price).toLocaleString('en-US', { maximumFractionDigits: 0 })}৳`}
    </span>
    {offerPrice && (
      <div className="flex items-center gap-1">
        <span className="text-xs sm:text-sm text-gray-400 line-through">
          {`${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}৳`}
        </span>
      </div>
    )}
  </div>
);

// Wishlist Product Card - 100% same design as original ProductCard
const WishlistProductCard = ({ product, onRemove }) => {
  const { id, name, imageUrl, price, offerPrice, tag, variants, rating, reviews } = product;
  
  const navigate = useNavigate();
  const { openProductDrawer } = useAppStore();

  const isActionDisabled = tag?.type === 'Sold Out' || tag?.type === 'comingSoon';
  
  const buttonText = React.useMemo(() => {
    if (tag?.type === 'Sold Out') return 'Sold Out';
    if (tag?.type === 'comingSoon') return 'Coming Soon';
    if (variants && variants.length > 0) return 'Choose Options';
    return 'Order Now';
  }, [tag, variants]);

  const hasVariants = React.useMemo(() => {
    return variants && variants.length > 0;
  }, [variants]);

  const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(id);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isActionDisabled) return;
    
    if (hasVariants) {
      navigate(`/shop/${id}/${slugify(name)}`);
    } else {
      openProductDrawer(product);
    }
  };

  const handleProductClick = (e) => {
    e.preventDefault();
    navigate(`/shop/${id}/${slugify(name)}`);
  };

  return (
    <div 
      className="w-full bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 ease-in-out border border-gray-100 overflow-hidden flex flex-col cursor-pointer"
      onClick={handleProductClick}
    >
      <div className="relative overflow-hidden">
        {tag && <Tag text={tag.text} color={tag.color} />}
        
        {/* Remove Button (Cross Icon) */}
        <button
          onClick={handleRemove}
          className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-white/20 backdrop-blur-sm text-gray-800 hover:text-red-500 transition-all duration-300 hover:scale-110"
          aria-label="Remove from wishlist"
        >
          <svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            strokeWidth="2.5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Product Image - Same as original */}
        <img
          src={`${BASE_URL}${imageUrl}`}
          alt={name}
          className="w-full h-auto aspect-square object-cover transition-transform duration-300 hover:scale-105"
        />
      </div>

      {/* Product Info - Same structure as original ProductCard */}
      <div className="p-3 flex flex-col flex-grow pt-">
        <div className="text-sm sm:text-[14px] font-bold text-gray-800 truncate" title={name}>
          {name}
        </div>

        {/* Rating Display - Same as original */}
        <div className="flex items-center gap-1 mt-0.5">
          <div className="flex items-center gap-[2px]">
            {[...Array(5)].map((_, index) => {
              let fillPercent = 0;

              if (rating && rating > 0) {
                const starValue = rating - index;
                if (starValue >= 0.9) fillPercent = 100;
                else if (starValue >= 0.4) fillPercent = 70;
                else if (starValue >= 0.1) fillPercent = 30;
              }

              return (
                <div key={index} className="relative w-3 h-3">
                  <StarIcon className="absolute top-0 left-0 w-3 h-3 text-gray-300" />
                  {fillPercent > 0 && (
                    <div
                      className="absolute top-0 left-0 h-3 overflow-hidden"
                      style={{ width: `${fillPercent}%` }}
                    >
                      <StarIcon className="w-3 h-3 text-yellow-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Rating Text - Same as original */}
          <span className="text-[11px] font-semibold text-gray-700">
            {reviews > 0 ? rating.toFixed(1) : ''}
          </span>
          <span className="text-[11px] text-gray-500">
            {reviews > 0 ? `(${reviews})` : '(Not Rated)'}
          </span>
        </div>

        {/* Price and Return Policy - Same as original */}
        <div className="mt-auto">
          <PriceDisplay price={price} offerPrice={offerPrice} />
          <div className="flex items-center gap-1 mt-1 text-[10px] font-medium text-emerald-600">
            <ReturnIcon className="w-3 h-3 flex-shrink-0" />
            <span>3 days easy return policy</span>
          </div>
        </div>
      </div>
      
      {/* Add to Cart Button - Same as original */}
      <div className="px-3 pb-3">
        <button
          onClick={handleButtonClick}
          disabled={isActionDisabled}
          className="w-full px-4 py-2 text-xs sm:text-sm font-bold bg-gray-900 text-white rounded-lg transition duration-300 ease-in-out hover:bg-gray-700 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-gray-900"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyWishlist = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-[450px] mx-auto flex flex-col items-center justify-center px-6 bg-gray-50" style={{ height: 'calc(100dvh - 110px)'}}>
      {/* Lottie Animation */}
      <div className="w-62 h-62 mb-1">
        <DotLottieReact
          src="https://lottie.host/99fce016-ac7c-4f2b-a0b1-0d84db2ca55f/6dcFTuE4Fa.lottie"
          loop
          autoplay
        />
      </div>

      {/* Empty State Text */}
      <h2 className="text-lg font-medium text-gray-500 mb-2 text-center">
        Wishlist is Empty
      </h2>
      <p className="text-sm text-gray-400 mb-6 text-center">
        Save your favorite items here to keep track of what you love.
      </p>

      {/* Shop Now Button */}
      <button
        className="bg-gray-900 hover:bg-black text-white font-medium py-1 px-6 rounded-lg flex items-center gap-2 transition-colors duration-200"
        onClick={() => navigate('/shop')}
      >
        Shop Now
      </button>
    </div>
  );
};

// Main WishlistPage Component
const WishlistPage = () => {
  const { 
    wishlistItems = [], 
    wishlistCount = 0, 
    removeFromWishlist 
  } = useAppStore();
  
  const navigate = useNavigate();

  const handleRemoveItem = (id) => {
    removeFromWishlist(id);
  };

  // Empty State
  if (!wishlistItems || wishlistItems.length === 0) {
    return (
      <div className="h-[calc(100dvh-108px)] bg-gray-50">
        <EmptyWishlist />
      </div>
    );
  }

  // Main Wishlist View
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-[450px] relative flex flex-col mx-auto">
        {/* Header */}
        <div className="h-[54px] flex-shrink-0 bg-white border-b border-gray-100 px-5 flex items-center justify-start border rounded-b-xl">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-500 hover:text-black transition-colors rounded-full hover:bg-gray-50"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-gray-600 tracking-wide">Wishlist ({wishlistCount} items)</h1>
        </div>

        {/* Wishlist Items Container - Grid layout with 2 columns */}
        <div className="flex-grow overflow-y-auto py-[10px] bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            {wishlistItems.map((item) => (
              <WishlistProductCard
                key={item.id}
                product={item}
                onRemove={handleRemoveItem}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WishlistPage;